<template>
  <div v-if="team && user">
    <share-template-instructions-alert :template="template">
    </share-template-instructions-alert>
    <v-card class="mb-4">
      <v-card-text v-if="isScheduleEmailsEnabled || canWriteCustomMessage || canShareLinks">

        <template v-if="isScheduleEmailsEnabled">
          <v-radio-group v-model="form.is_scheduled"
                         label="When do you want to send this campaign?"
                         class="mt-0"
                         @change="changeScheduling">
            <v-radio :value="false"
                     label="Send immediately">
            </v-radio>
            <v-radio :value="true"
                     label="Schedule for delivery in the future">
            </v-radio>
          </v-radio-group>

          <div v-if="form.is_scheduled"
               class="d-flex align-center mb-4">
            <v-dialog ref="dateDialog"
                      v-model="showDatePicker"
                      :return-value.sync="form.scheduled_date"
                      persistent
                      width="290px">
              <template v-slot:activator="{on, attrs}">
                <v-text-field :value="computedDateFormat"
                              label="Date"
                              outlined
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              style="max-width: 300px;"
                              hide-details
                              class="mr-4"
                              :error-messages="form.errors.get('scheduled_at')">
                </v-text-field>
              </template>
              <v-date-picker v-model="form.scheduled_date"
                             :allowed-dates="allowedDates"
                             :min="today">
                <v-btn text
                       @click="showDatePicker = false">
                  Cancel
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text
                       color="primary"
                       @click="$refs.dateDialog.save(form.scheduled_date)">
                  OK
                </v-btn>
              </v-date-picker>
            </v-dialog>
            <v-dialog ref="timeDialog"
                      v-model="showTimePicker"
                      :return-value.sync="form.scheduled_time"
                      persistent
                      width="290px">
              <template v-slot:activator="{on, attrs}">
                <v-text-field :value="computedTimeFormat"
                              label="Time"
                              outlined
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              style="max-width: 180px;"
                              hide-details
                              class="mr-4"
                              :error-messages="form.errors.get('scheduled_time')">
                </v-text-field>
              </template>
              <v-time-picker v-model="form.scheduled_time"
                             ampm-in-title>
                <v-btn text
                       @click="showTimePicker = false">
                  Cancel
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text
                       color="primary"
                       @click="$refs.timeDialog.save(form.scheduled_time)">
                  OK
                </v-btn>
              </v-time-picker>
            </v-dialog>
          </div>
        </template>

        <template v-if="canWriteCustomMessage">
          <v-textarea v-model="form.custom_message"
                      clearable
                      autocapitalize="off"
                      spellcheck="false"
                      :label="isCustomMessageRequired ? 'Write a custom message (required)' : 'Write a custom message (optional)'"
                      :hint="customMessageHint"
                      counter
                      maxlength="1000"
                      persistent-hint
                      :required="isCustomMessageRequired"
                      :error-messages="form.errors.get('custom_message')"
                      rows="2"
                      @blur="preview(contacts[0])"
                      class="pt-0 mb-4">
          </v-textarea>
        </template>

        <template v-if="canShareLinks">
          <v-btn @click.stop="openContentPicker">
            Insert links
          </v-btn>
          <content-picker-dialog v-if="openContentPicker"
                                 :dialog="showContentPicker"
                                 :form="form"
                                 @close="closeContentPicker"
                                 @insert="createLinks">
          </content-picker-dialog>

          <v-list v-if="selectedPresentations.length">
            <v-subheader>Links to these files will be included in the email where the <code class="mx-1"><span v-pre>{{ shared_links }}</span></code> tag is.</v-subheader>
            <v-divider></v-divider>
            <template v-for="presentation in selectedPresentations">
              <v-list-item :key="presentation.id">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ presentation.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-list>
        </template>

      </v-card-text>
      <v-card-actions class="px-4">
        <v-btn @click.prevent="goBack"
               :disabled="loading">
          Go back
        </v-btn>
        <v-btn @click.prevent="submit"
               :disabled="loading"
               color="primary">
          <template v-if="isScheduled">
            Schedule Delivery
          </template>
          <template v-else>
            Send Now
          </template>
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-row>
      <v-col cols="12"
             sm="4">
        <v-card>
          <v-card-title class="pb-0">
            {{ subscribers.length | pluralize('Recipient') }}
          </v-card-title>
          <v-virtual-scroll :items="contacts"
                            :item-height="61"
                            height="300">
            <template v-slot:default="{item: contact}">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ contact.greeting }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ contact.email }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn @click.prevent="preview(contact)"
                         small>
                    Preview
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-virtual-scroll>
          <v-card-actions v-if="1===2">
            <v-btn @click.prevent="goBack"
                   :disabled="loading">
              Go back
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn @click.prevent="submit"
                   :disabled="loading"
                   color="primary">
              Send Campaign
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12"
             sm="8">
        <v-card>
          <v-toolbar color="primary"
                     dark
                     flat
                     tile>
            <v-toolbar-title>
              Preview
            </v-toolbar-title>
            <v-progress-circular indeterminate
                                 class="ml-4"
                                 v-if="loading">
            </v-progress-circular>
          </v-toolbar>
          <v-card-text v-if="preview_subject">
            <div>
              From: {{ fromName }}
            </div>
            <div>
              Reply To: {{ user.email }}
            </div>
            <div>
              To: {{ preview_contact.email }}
            </div>
            <div>
              Subject: {{ preview_subject }}
            </div>
            <v-divider></v-divider>
          </v-card-text>
          <v-responsive v-if="dataURI"
                        class="mx-4 embed-responsive"
                        :aspect-ratio="4/3">
            <iframe :src="dataURI">
            </iframe>
          </v-responsive>
        </v-card>
      </v-col>
    </v-row>

      <v-dialog v-model="confirmDialog"
                persistent
                width="360">
        <v-card>
          <v-card-title>

          </v-card-title>
          <v-card-text class="pb-0">
            <p>
              {{ confirmQuestion }}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary"
                   @click.prevent="send">
              Yes
            </v-btn>
            <v-btn @click.prevent="cancelSend">
              No
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {find} from 'lodash';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import mailcoachService from '../../../services/mailcoachService';
import moment from 'moment-timezone';
import ShareTemplateInstructionsAlert from "./components/ShareTemplateInstructionsAlert";
import ContentPickerDialog from '../../emails/wizard/components/ContentPickerDialog';
import DOMPurify from 'dompurify';
import htmlService from '../../../services/htmlService';

export default {
  name: 'email-campaign-review',
  components: {ContentPickerDialog, ShareTemplateInstructionsAlert},
  data: vm => ({
    form: new Form({
      list_id: null,
      template_id: null,
      // MLD-3412
      is_scheduled: false,
      scheduled_at: null,
      scheduled_date: moment().format('YYYY-MM-DD'),
      scheduled_time: moment().add(5, 'minute').format('HH:mm'),
      // @see https://attacomsian.com/blog/javascript-current-timezone
      scheduled_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      custom_message: null, // MLD-3998
      presentations: [], // MLD-4004 Generate shared links for these presentations.
    }),
    confirmDialog: false, // MLD-4283
    loading: false,
    preview_contact: null,
    preview_content: null,
    preview_from_address: null,
    preview_from_name: null,
    preview_subject: null,
    subscribers: [],
    showDatePicker: false,
    showTimePicker: false,
    showContentPicker: false, // MLD-4004
    showCannotShareDialog: false, // MLD-4004
    selectedPresentations: [],
  }),
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('content', [ // MLD-4004
      'presentations',
    ]),
    ...mapGetters('emailCampaignCart', [
      'list',
      'template',
    ]),
    canShareLinks() { // MLD-4004
      return this.team.sharing_enabled
        && this.template
        && this.template.can_share_links
        && this.template.has_shared_links_tag;
    },
    customMessageHint() {
      if (this.isCustomMessageRequired) {
        return 'You must write a custom message to put in the email. The message will be the same for everyone who receives it, so don\'t personalize it. Make sure to follow your company\'s rules for what you can and can\'t write. You\'re not allowed to include links or fancy formatting like bold or italics.'
      } else if (this.isCustomMessageAllowed) {
        return 'Write a custom message to put in the email. The message will be the same for everyone who receives it, so don\'t personalize it. Make sure to follow your company\'s rules for what you can and can\'t write. You\'re not allowed to include links or fancy formatting like bold or italics.'
      }
      return null
    },
    isScheduleEmailsEnabled() {
      return this.team.schedule_emails_enabled;
    },
    canWriteCustomMessage() { // MLD-3998
      return this.template
        && this.template.is_custom_message_allowed
        && this.template.has_custom_message_tag;
    },
    isCustomMessageAllowed() { // MLD-3998
      return this.template
        && this.template.is_custom_message_allowed;
    },
    isCustomMessageRequired() { // MLD-3998
      return this.isCustomMessageAllowed
        && this.template.is_custom_message_required;
    },
    today() {
      return moment().startOf('day').format('YYYY-MM-DD');
    },
    contacts() {
      return this.subscribers.map(s => s.contact);
    },
    fromName() {
      if (this.preview_from_name && this.preview_from_address) {
        return `${this.preview_from_name} <${this.preview_from_address}>`
      }
      return this.user.name;
    },
    dataURI() {
      if (this.preview_content) {
        return htmlService.dataURI(this.preview_content);
      }
      return null;
    },
    // MLD-3412
    isScheduled() {
      return this.form.is_scheduled;
    },
    hasScheduledValues() {
      return !!(this.form.scheduled_date && this.form.scheduled_time);
    },
    scheduledAt() {  // Local time
      if (this.hasScheduledValues) {
        return moment(`${this.form.scheduled_date} ${this.form.scheduled_time}:00`, 'YYYY-MM-DD HH:mm:00', true);
      }
      return null;
    },
    scheduledAtUTC() { // UTC time
      const scheduled = this.scheduledAt;
      if (scheduled) {
        return scheduled.clone().utc();
      }
      return null;
    },
    // @see https://vuetifyjs.com/en/components/date-pickers/#formatting
    computedDateFormat() {
      if (this.form.scheduled_date) {
        try {
          return moment(this.form.scheduled_date, 'YYYY-MM-DD').format('dddd, MMMM D, YYYY');
        } catch (e) {
          console.log('computedDateFormat', e);
        }
      }
      return null;
    },
    computedTimeFormat() {
      if (this.form.scheduled_date && this.form.scheduled_time) {
        try {
          return moment(`${this.form.scheduled_date} ${this.form.scheduled_time}`, 'YYYY-MM-DD HH:mm').format('h:mm a');
        } catch (e) {
          console.log('computedTimeFormat', e);
        }
      }
      return null;
    },
    confirmQuestion() {
      return this.isScheduled
        ? `Are you sure you want to send this campaign at ${this.formattedScheduledAt}?`
        : 'Are you sure you want to send this campaign now?';
    },
    formattedScheduledAt() {
      return this.isScheduled
        ? this.scheduledAt?.format('h:mm a [on] dddd, MMMM D, YYYY')
        : null;
    }
  },
  created() {
    if (!this.template) {
      this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaign.wizard.template`, query: this.$route.query});
      return;
    }
    if (!this.list) {
      this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaign.wizard.recipients`, query: this.$route.query});
      return;
    }
    this.init();
  },
  methods: {
    async init() {
      if (this.team.schedule_emails_enabled === false) {
        this.form.is_scheduled = false;
      }
      this.form.scheduled_at = this.scheduledAtUTC;
      this.form.list_id = this.list.id;
      this.form.template_id = this.template.id;
      this.subscribers = await mailcoachService.getActiveListSubscribers(this.list.id);
      this.preview_contact = {
        email: 'TBD',
      }
      this.preview_content = null;//this.template.full_html;
      this.preview_subject = this.template.subject;
      if (this.contacts.length) {
        this.preview(this.contacts[0]);
      }
    },
    preview(contact) {
      this.loading = true;
      const form = {
        recipient: contact,
        custom_message: this.form.custom_message, // MLD-3998
      }
      shareTemplateService.render(this.template.id, form)
        .then(data => {
          this.preview_contact = contact;
          let html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}</head><body>${data.content}</body></html>`
          if (data.content?.startsWith('<body')) {
            html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}</head>${data.content}</html>`
          }
          this.preview_content = html
          this.preview_from_address = data.from_address;
          this.preview_from_name = data.from_name;
          this.preview_subject = data.subject;
        })
        .finally(() => {
          this.loading = false;
        })
    },
    goBack() {
      this.$store.dispatch('emailCampaignCart/prevStep');
      this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaign.wizard.recipients`});
    },
    changeScheduling() { // MLD-4115
      if (this.isScheduled) {
        this.form.scheduled_at = null;
        this.form.scheduled_date = moment().format('YYYY-MM-DD');
        this.form.scheduled_time = moment().add(5, 'minute').format('HH:mm');
      } else {
        this.form.scheduled_at = null;
        this.form.scheduled_date = null;
        this.form.scheduled_time = null;
      }
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      if (this.isScheduled && !this.scheduledAtUTC) {
        return;
      }
      if (this.scheduledAt) {
        if (this.scheduledAt.isBefore(moment().startOf('day'))) {
          this.form.errors.set('scheduled_at', ['The scheduled date & time has to be in the future.']);
          return;
        }
        if (this.scheduledAt.isBefore(moment())) {
          this.form.errors.set('scheduled_time', ['Cannot be in the past.']);
          return;
        }
      }
      this.showConfirmDialog();
    },
    showConfirmDialog() { // MLD-4283
      this.confirmDialog = true;
    },
    cancelSend() { // MLD-4283
      this.confirmDialog = false;
    },
    send() { // MLD-4283
      this.confirmDialog = false;
      this.form.scheduled_at = this.scheduledAtUTC;
      this.form.post(`/api/mailcoach/campaigns/send`)
        .then(({data}) => {
          if (data.is_scheduled) {
            this.$toast.success(`Your campaign will be sent at ${DOMPurify.sanitize(this.formattedScheduledAt)}`);
          } else if (data.is_sent || data.is_sending || data.is_queued) {
            this.$toast.success('Your campaign will be sent immediately!');
          } else {
            this.$toast.error('Your campaign could not be sent.');
          }
          this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaigns`});
        })
        .catch(err => {
          console.log('Campaign send error', err.message);
        })
    },
    closeContentPicker() { // MLD-4004
      console.log('closeContentPicker()');
      this.showContentPicker = false;
    },
    openContentPicker() { // MLD-4004
      console.log('openContentPicker()');
      this.form.presentations = [];
      this.showContentPicker = true;
    },
    createLinks() {
      console.log('createLinks()');
      this.closeContentPicker();
      try {
        this.selectedPresentations = [];
        this.form.presentations.forEach(id => {
          const p = find(this.presentations, p => p.id === id);
          if (p) {
            this.selectedPresentations.push(p);
          }
        })
      } catch (e) {
        console.log(e.message);
      }
    },
    // MLD-3412
    // @see https://vuetifyjs.com/en/components/date-pickers/#allowed-dates
    allowedDates: val => {
      console.log(val);
      return true;
    },
    // @see https://vuetifyjs.com/en/components/time-pickers/#allowed-times
    allowedHours: v => true,
    allowedMinutes: v => true,
    allowedStep: m => m % 10 === 0,
  }
}
</script>
